/**
 * ApplyClosureDialog — "Betriebsurlaub auf Mitarbeiter anwenden"
 * (POST /absences/apply-closure, Ein-Klick-Äquivalent der Ninox-IB-Massenaktion).
 *
 * - Zeitraum mit BusinessDayRangePicker (vorbelegt aus dem Aufrufer, z. B.
 *   BETRIEBSURLAUB-Eintrag des Betriebskalenders).
 * - Grund-Select: nur die drei erlaubten Gründe (URLAUB | FREI_NACH_MTV |
 *   UNBEZAHLTER_URLAUB), sonst 422 vom Backend.
 * - Mitarbeiter-Auswahl: Default "Alle aktiven" (mit Zähler), alternativ
 *   Multi-Select aus der aktiven Mitarbeiterliste.
 * - Switch "Hauptstelle automatisch zuordnen" (positionsAuto).
 * - Ergebnis-Ansicht: appliedCount/coveredBusinessDays prominent +
 *   Skipped-Tabelle (Name, Grund-Badge, Detail). Idempotent: zweiter Lauf
 *   desselben Zeitraums liefert ALREADY_ABSENT statt Duplikaten.
 *
 * Gate: personal_schreiben (Ein-/Ausblenden; durchgesetzt wird serverseitig).
 */

import { useEffect, useMemo, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { BusinessDayRangePicker } from "@/components/shared"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { DataGrid, type ColumnDef } from "@/components/ui/data-grid"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select } from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { ABSENCE_REASONS } from "@/lib/absenceColors"
import { fmtDays } from "@/lib/format"
import { useCan } from "@/lib/permissions"
import { api, apiError } from "@/services/api"
import type { AbwesenheitGrund, ApplyClosureResponse, ApplyClosureSkipReason } from "@/types"

/** Erlaubte Gründe (Spiegel von CLOSURE_REASONS im Backend) */
const CLOSURE_REASONS: AbwesenheitGrund[] = ["URLAUB", "FREI_NACH_MTV", "UNBEZAHLTER_URLAUB"]

const SKIP_REASON_META: Record<ApplyClosureSkipReason, { label: string; badge: string }> = {
  ALREADY_ABSENT: {
    label: "Bereits abwesend",
    badge: "border-yellow-300 bg-yellow-100 text-yellow-800",
  },
  NOT_ACTIVE: { label: "Nicht aktiv", badge: "border-gray-300 bg-gray-100 text-gray-600" },
  NO_BUSINESS_DAYS: {
    label: "Keine Betriebstage",
    badge: "border-slate-300 bg-slate-200 text-slate-800",
  },
}

interface ApplyClosureDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Zeitraum-Vorbelegung (z. B. aus dem BETRIEBSURLAUB-Kalendereintrag) */
  defaultFrom?: string
  defaultTo?: string
}

export function ApplyClosureDialog({
  open,
  onOpenChange,
  defaultFrom = "",
  defaultTo = "",
}: ApplyClosureDialogProps) {
  const queryClient = useQueryClient()
  const canWrite = useCan("personal_schreiben")

  const [from, setFrom] = useState(defaultFrom)
  const [to, setTo] = useState(defaultTo)
  const [reason, setReason] = useState<AbwesenheitGrund>("URLAUB")
  const [scope, setScope] = useState<"all" | "selected">("all")
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [positionsAuto, setPositionsAuto] = useState(false)
  const [result, setResult] = useState<ApplyClosureResponse | null>(null)

  // Beim (Wieder-)Öffnen frisch vorbelegen
  useEffect(() => {
    if (!open) return
    setFrom(defaultFrom)
    setTo(defaultTo)
    setReason("URLAUB")
    setScope("all")
    setSelected(new Set())
    setPositionsAuto(false)
    setResult(null)
    // defaultFrom/defaultTo absichtlich nicht als Deps: nur der Open-Moment zählt
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const employees = useQuery({
    queryKey: ["employees", { size: 100, status: "active" }],
    queryFn: () => api.employees.list({ size: 100, status: "active" }),
    enabled: open,
  })
  const activeEmployees = useMemo(() => employees.data?.data ?? [], [employees.data])

  const apply = useMutation({
    mutationFn: () =>
      api.absences.applyClosure({
        from,
        to,
        reason,
        employeeIds:
          scope === "selected" ? [...selected].map((id) => Number(id)) : undefined,
        positionsAuto,
      }),
    onSuccess: (data) => {
      setResult(data)
      queryClient.invalidateQueries({ queryKey: ["absences"] })
      queryClient.invalidateQueries({ queryKey: ["employees"] })
      toast.success(
        `Betriebsurlaub angewendet: ${data.appliedCount} von ${data.totalTargets} Mitarbeitern`
      )
    },
    onError: (e) => toast.error(apiError(e)),
  })

  function toggle(id: string) {
    setSelected((cur) => {
      const next = new Set(cur)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const valid =
    from !== "" && to !== "" && from <= to && (scope === "all" || selected.size > 0)

  // Grid-Spalten der Skipped-Tabelle: header als String (Spalten-Auswahl),
  // Grund sortiert/filtert ueber das deutsche Label, Zelle rendert das Badge
  const skippedColumns = useMemo<ColumnDef<ApplyClosureResponse["skipped"][number]>[]>(
    () => [
      {
        id: "employee",
        header: "Mitarbeiter",
        accessorFn: (s) => s.employeeName,
        meta: { cellClassName: "font-medium" },
      },
      {
        id: "reason",
        header: "Grund",
        accessorFn: (s) => SKIP_REASON_META[s.reason].label,
        cell: ({ row }) => (
          <Badge variant="outline" className={SKIP_REASON_META[row.original.reason].badge}>
            {SKIP_REASON_META[row.original.reason].label}
          </Badge>
        ),
      },
      {
        id: "detail",
        header: "Detail",
        accessorFn: (s) => s.detail ?? "—",
        meta: { cellClassName: "text-muted-foreground" },
      },
    ],
    []
  )

  if (!canWrite) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Betriebsurlaub auf Mitarbeiter anwenden</DialogTitle>
          <DialogDescription>
            Bucht EINE Abwesenheit je Zielmitarbeiter über den Zeitraum; bereits abwesende
            Mitarbeiter werden übersprungen (idempotent).
          </DialogDescription>
        </DialogHeader>

        {result ? (
          // -------------------------------------------------------------------
          // Ergebnis-Ansicht
          // -------------------------------------------------------------------
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-md border bg-muted/40 p-3 text-center">
                <p className="text-2xl font-semibold tabular-nums" data-testid="applied-count">
                  {result.appliedCount}
                  <span className="text-sm font-normal text-muted-foreground">
                    {" "}
                    / {result.totalTargets}
                  </span>
                </p>
                <p className="text-xs text-muted-foreground">Mitarbeiter angewendet</p>
              </div>
              <div className="rounded-md border bg-muted/40 p-3 text-center">
                <p className="text-2xl font-semibold tabular-nums" data-testid="covered-days">
                  {fmtDays(result.coveredBusinessDays)}
                </p>
                <p className="text-xs text-muted-foreground">Betriebstage je Mitarbeiter</p>
              </div>
            </div>

            {result.skipped.length > 0 ? (
              <div>
                <p className="mb-1 text-sm font-medium">
                  Übersprungen ({result.skipped.length})
                </p>
                <div
                  className="max-h-64 overflow-y-auto rounded-md border p-2"
                  data-testid="skipped-table"
                >
                  {/* Ergebnis-Dialog: keine Persistenz noetig — bewusst ohne storageKey */}
                  <DataGrid
                    columns={skippedColumns}
                    data={result.skipped}
                    emptyMessage="Kein Mitarbeiter übersprungen."
                  />
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Kein Mitarbeiter übersprungen.
              </p>
            )}
          </div>
        ) : (
          // -------------------------------------------------------------------
          // Formular
          // -------------------------------------------------------------------
          <div className="grid gap-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="apply-closure-from">Von *</Label>
                <Input
                  id="apply-closure-from"
                  type="date"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="apply-closure-to">Bis *</Label>
                <Input
                  id="apply-closure-to"
                  type="date"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                />
              </div>
            </div>

            <BusinessDayRangePicker
              from={from}
              to={to}
              onChange={(f, t) => {
                setFrom(f)
                setTo(t)
              }}
            />

            <div className="space-y-1.5">
              <Label htmlFor="apply-closure-reason">Grund</Label>
              <Select
                id="apply-closure-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value as AbwesenheitGrund)}
              >
                {CLOSURE_REASONS.map((r) => (
                  <option key={r} value={r}>
                    {ABSENCE_REASONS[r].label}
                  </option>
                ))}
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Mitarbeiter</Label>
              <div className="flex flex-col gap-1.5">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="apply-closure-scope"
                    className="size-4 accent-primary"
                    checked={scope === "all"}
                    onChange={() => setScope("all")}
                  />
                  Alle aktiven Mitarbeiter
                  <span className="text-muted-foreground">
                    ({employees.isLoading ? "…" : activeEmployees.length})
                  </span>
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="apply-closure-scope"
                    className="size-4 accent-primary"
                    checked={scope === "selected"}
                    onChange={() => setScope("selected")}
                  />
                  Auswahl
                  {scope === "selected" && (
                    <span className="text-muted-foreground">({selected.size} gewählt)</span>
                  )}
                </label>
              </div>
              {scope === "selected" && (
                <div className="max-h-44 overflow-y-auto rounded-md border p-2">
                  {activeEmployees.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      {employees.isLoading ? "Lade Mitarbeiter …" : "Keine aktiven Mitarbeiter."}
                    </p>
                  ) : (
                    activeEmployees.map((emp) => (
                      <label
                        key={emp.id}
                        className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-sm hover:bg-accent"
                      >
                        <input
                          type="checkbox"
                          className="size-4 accent-primary"
                          checked={selected.has(emp.id)}
                          onChange={() => toggle(emp.id)}
                        />
                        {emp.displayName}
                      </label>
                    ))
                  )}
                </div>
              )}
            </div>

            <label className="flex items-center gap-2 text-sm">
              <Switch checked={positionsAuto} onCheckedChange={setPositionsAuto} />
              Hauptstelle automatisch zuordnen
            </label>
          </div>
        )}

        <DialogFooter>
          {result ? (
            <>
              <Button variant="outline" onClick={() => setResult(null)}>
                Neue Anwendung
              </Button>
              <Button onClick={() => onOpenChange(false)}>Schließen</Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Abbrechen
              </Button>
              <Button disabled={!valid || apply.isPending} onClick={() => apply.mutate()}>
                Anwenden
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
